import Box from '@mui/material/Box';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSnackbar } from 'notistack';
import AlertMessages from 'common/Alert/AlertMessages';
import { useTranslation } from 'react-i18next';
import { checkPrivileges } from 'Helpers/CheckPrivileges';
import BackendPaginationTable from 'common/Table/TableWithPagination';
import { useNavigate, useParams } from 'react-router-dom';
import ComponentHeaderTwo from 'common/ComponentHeaderTwo/ComponentHeaderTwo';
import { ReactComponent as Groups  } from "assets/SideBarIcons/Groups.svg";
import { useGetPackageGroupPackageService, useAssignPackageService, useUnassignPackageService, useAssignAllPackageService, useUnassignAllPackageService } from 'subModule/src/services/packagegroup/usePackageGroup';
import PackageGroupTabs from './PackageGroupTabs';
import PackageGroupPackageSearchHeader from 'components/PackageGroup/PackageGroupPackageSearchHeader';
import { SelectChangeEvent } from '@mui/material';
import {
    getSortParamFromColumn,
    PACKAGE_SORT_FIELD_MAP,
    SortOrder,
} from 'Helpers/buildApiSortParam';
import type { BreadcrumbItem } from 'Helpers/generalBreadcrumbs';
import { pickProfileEntityTitle } from 'Helpers/pickProfileEntityTitle';
import { PackageGroupProfileService } from 'subModule/src/core/services';

const PACKAGE_GROUP_SORTABLE_COLUMNS = ['title', 'packageCode'];
 
interface SearchFormData {
    [key: string]: string | any;
}
export default function PackageGroupPackageTable() {
    const params = useParams();
    const groupId = params?.id;
    const navigate = useNavigate();
    const [groupTitle, setGroupTitle] = useState('');
    const [pageNo, setPageNo] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(20);
    const [loading, setLoading] = useState<boolean>(false);
    const [filter, setFilter] = useState<string[]>(['title']);
    const [search, setSearch] = useState<any>({});
    const [sort, setSort] = useState<string | undefined>();
    const [sortOrderBy, setSortOrderBy] = useState('');
    const [sortOrder, setSortOrder] = useState<SortOrder>('asc');
    const [searchFormData, setSearchFormData] = useState<SearchFormData>({ title: '', packageCode: '' });
    const { data: tableData, isFetching, refetch } = useGetPackageGroupPackageService({ pageNumber: pageNo, size: pageSize, ...search, sort }, +(params as any).id);
    const { enqueueSnackbar } = useSnackbar();
    const { t } = useTranslation();

    useEffect(() => {
        if (!groupId) return;
        let active = true;
        PackageGroupProfileService.getPackageGroupById(+groupId)
            .then((res: unknown) => {
                if (!active) return;
                const name = pickProfileEntityTitle(res);
                if (name) setGroupTitle(name);
            })
            .catch(() => {});
        return () => {
            active = false;
        };
    }, [groupId]);

    const breadcrumbItems: BreadcrumbItem[] = useMemo(
        () => [
            { labelKey: 'Items' },
            { labelKey: 'packagesGroup', href: '/packages-group' },
            {
                labelKey: 'title',
                ...(groupTitle ? { label: groupTitle } : {}),
                href: groupId ? `/package-group/${groupId}` : undefined,
            },
            { labelKey: 'assignPackage' },
        ],
        [groupId, groupTitle],
    );

    let allPackagesAssigned: unknown = tableData?.pages[0].data?.content.map(packages => packages.linked).every(linked => linked === true);
    const fetchData = useCallback(() => {
        refetch().then((res: any) => {
            if (!res?.data?.pages[0]?.success) enqueueSnackbar(<AlertMessages text={res?.data?.pages[0]?.message || "Something went wrong"} />, { variant: "error" });
        });
    }, []);
    useEffect(() => {
        fetchData();
    }, [pageNo, pageSize, search, sort]);
    const handleChangePage = (pageNo: number) => {
        setPageNo(pageNo + 1);
    }
    const handleChangeRows = (pageSize: number) => {
        setPageSize(pageSize);
        setPageNo(1);
    }
    const clearSearch = () => {
        setFilter(['title']);
        setSearchFormData({ title: '', packageCode: '' });
        if (Object.keys(search).length) {
            setSearch({});
        }
    }
    const handleChangeFilter = (event: SelectChangeEvent<typeof filter>) => {
        const { target: { value }, } = event;
        setFilter(typeof value === 'string' ? value.split(',') : value,);
        setSearchFormData(prevFormData =>
            Object.fromEntries(Object.keys(prevFormData).map(key =>
                [key, value.includes(key) ? prevFormData[key] : '']
            ))
        );
        if (!value.length && Object.keys(search).length) {
            clearSearch();
        }
    };
    const handleChangeSearch = (field: string, value: any) => {
        const val = value;
        setSearchFormData((prevData) => ({
            ...prevData,
            [field]: val,
        }));
    };
    const handleSubmitSearch = () => {
        const filteredSearch: Record<string, string> = {};
        Object.entries(searchFormData).forEach(([key, value]) => {
            if (value) {
                filteredSearch[key] = value?.trim();
            }
        });
        if (Object.keys(filteredSearch).length > 0 && Object.values(filteredSearch).some(value => value)) {
            setSearch(filteredSearch);
            setPageNo(1);
            setPageSize(pageSize);
        }
    };
    const handleSortChange = (orderBy: string, order: SortOrder) => {
        const nextSort = getSortParamFromColumn(orderBy, order, PACKAGE_SORT_FIELD_MAP);
        if (!nextSort) return;
        setSortOrderBy(orderBy);
        setSortOrder(order);
        setSort(nextSort);
        setPageNo(1);
    };
    const assignUnassignSingleEntity = (isChecked: boolean, row: any) => {
        setLoading(true);
        if (isChecked) {
            onAssignPackage({ packageGroupId: +(params as any).id, packageId: row.id });
        } else {
            onUnassignPackage({ packageGroupId: +(params as any).id, packageId: row.id });
        }
    }
    const assignUnassignAllEntities = (isChecked: boolean) => {
        setLoading(true);
        if (isChecked) {
            onAssignAllPackages({ packageGroupId: +(params as any).id });
        } else {
            onUnassignAllPackages({ packageGroupId: +(params as any).id });
        }
    }
    const { onAssignPackage } = useAssignPackageService({
        onSuccess: async (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length === tableData?.pages[0].data.content.filter(packages => packages.linked).length) {
                    allPackagesAssigned = true;
                }
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('package') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignPackage } = useUnassignPackageService({
        onSuccess: async (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                if (tableData?.pages[0].data.content.length !== tableData?.pages[0].data.content.filter(packages => packages.linked).length) {
                    allPackagesAssigned = false;
                }
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('package') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });
    const { onAssignAllPackages } = useAssignAllPackageService({
        onSuccess: async (msg) => {
            const response: any = {};
            Object.keys(msg).forEach((key, index) => { response[key] = Object.values(msg)[index]; });
            if (response.success) {
                allPackagesAssigned = true;
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('packages') + ' ' + t('assigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
            if (err instanceof Error) {
                console.error(err.stack);
            }
        }
    })
    const { onUnassignAllPackages } = useUnassignAllPackageService({
        onSuccess: async (data) => {
            const response: any = {};
            Object.keys(data).forEach((key, index) => { response[key] = Object.values(data)[index]; });
            if (response.success) {
                allPackagesAssigned = false;
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} text={t('All') + ' ' + t('packages') + ' ' + t('unassigned') + ' ' + t('successfully')} />, { variant: 'success' });
            } else {
                await refetch();
                setLoading(false);
                enqueueSnackbar(<AlertMessages statusCode={response.status} />, { variant: 'error' });
            }
        },
        onError: (err: any) => {
            console.log("err====,  ", err);
            enqueueSnackbar(<AlertMessages statusCode={0} />, { variant: 'error' });
        },
    });

    return (
        <Box>
            <ComponentHeaderTwo
                showBtn={true}
                showSaveBtn={false}
                onClick={() => navigate('/packages-group')}
                heading={t('packagesGroup')}
                icon={<Groups height={28} width={28} />}
                btnText={"back"}
                btnType={"back"}
                showCreateBtn={false}
                breadcrumbItems={breadcrumbItems}
            />
            <Box>
                <div className='flex'>
                    {params?.id ? <PackageGroupTabs params={params} selectedIndex={1} /> : null}
                    <BackendPaginationTable
                        columns={[
                            { id: 'title', label: 'title', numeric: false },
                            { id: 'packageCode', label: 'packageCode', numeric: false },
                        ]}
                        data={tableData?.pages[0]?.data?.content || []}
                        pageNo={pageNo}
                        pageSize={pageSize}
                        tableData={tableData?.pages[0]?.data}
                        showCheckbox={true}
                        showActions={true}
                        enableSorting={true}
                        serverSideSorting={true}
                        sortOrderBy={sortOrderBy}
                        sortOrder={sortOrder}
                        onSortChange={handleSortChange}
                        sortableColumnIds={PACKAGE_GROUP_SORTABLE_COLUMNS}
                        showHeader={true}
                        showSearch={true}
                        showFilter={true}
                        showCreateButton={false}
                        actions={[]}
                        handleChangePage={handleChangePage}
                        handleChangeRows={handleChangeRows}
                        assignUnassignSingleEntity={assignUnassignSingleEntity}
                        assignUnassignAllEntities={assignUnassignAllEntities}
                        allEntitiesAssigned={allPackagesAssigned}
                        loadingSearch={isFetching || loading}
                        assignPrivilege={checkPrivileges('ROLE_PACKAGEGROUP_PACKAGE_ASSIGN')}
                        unassignPrivilege={checkPrivileges('ROLE_PACKAGEGROUP_PACKAGE_UNASSIGN')}
                        headerComponent={<PackageGroupPackageSearchHeader 
                            searchFormData={searchFormData}
                            handleChangeFilter={handleChangeFilter}
                            handleChangeSearch={handleChangeSearch}
                            handleSubmitSearch={handleSubmitSearch}
                            clearSearch={clearSearch}
                            search={search}
                            {...{filter}}
                        />}
                    />
                </div>
            </Box>
        </Box>
    )
}